// Onglet « Société ».
//
// Ces coordonnées sont imprimées sur chaque devis. Les cinq champs
// obligatoires ne sont pas un caprice de formulaire : sans eux, la
// génération d'un devis est REFUSÉE, parce qu'un document commercial
// belge doit porter la dénomination, l'adresse complète et le numéro de
// TVA. Aucune valeur de repli sur un document qui part chez un client.

import { useCallback, useEffect, useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import { TextField, Textarea } from '../../ui/TextField.jsx';
import Button from '../../ui/Button.jsx';
import { ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';

const REQUIRED = ['name', 'address', 'postal_code', 'city', 'vat'];

export function SocietePanel({ isAdmin }) {
  const toast = useToast();
  const load = useCallback(() => api.get('/parametres'), []);
  const { data, error, loading, reload } = useResource(load);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (data) setForm(data.settings.company ?? {});
  }, [data]);

  if (loading || !form) return <SkeletonRows rows={5} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const manquants = REQUIRED.filter((f) => !String(form[f] ?? '').trim());
  const set = (patch) => setForm({ ...form, ...patch });

  async function save(event) {
    event.preventDefault();
    setPending(true);
    setErrors({});
    try {
      await api.put('/parametres/company', form);
      toast.success('Coordonnées enregistrées.');
      reload();
    } catch (err) {
      if (err.details) setErrors(err.details);
      else toast.error(err.message);
    } finally {
      setPending(false);
    }
  }

  return (
    <Card>
      <CardHead title="Coordonnées de l'atelier">
        {isAdmin ? (
          <Button size="sm" variant="primary" type="submit" form="form-societe" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        ) : null}
      </CardHead>
      <CardBody>
        <form id="form-societe" className="stack" onSubmit={save}>
          {manquants.length > 0 ? (
            <div className="warnings" role="alert">
              <p className="caps">{manquants.length} champ(s) obligatoire(s) manquant(s)</p>
              <p>
                Tant qu’ils ne sont pas renseignés, <strong>aucun devis ne peut être généré</strong>.
                Un document commercial doit porter la dénomination, l’adresse complète et le numéro
                de TVA de l’émetteur.
              </p>
            </div>
          ) : null}

          <TextField label="Dénomination" required value={form.name ?? ''} error={errors.name}
            disabled={!isAdmin} onChange={(e) => set({ name: e.target.value })} />
          <div className="grid-2">
            <TextField label="Mention sous le nom" value={form.tagline ?? ''} error={errors.tagline}
              disabled={!isAdmin} hint="Apparaît en cyan sous la dénomination, sur le bandeau."
              onChange={(e) => set({ tagline: e.target.value })} />
            <TextField label="Numéro de TVA" required value={form.vat ?? ''} error={errors.vat}
              disabled={!isAdmin} onChange={(e) => set({ vat: e.target.value })} />
          </div>
          <TextField label="Adresse" required value={form.address ?? ''} error={errors.address}
            disabled={!isAdmin} onChange={(e) => set({ address: e.target.value })} />
          <div className="grid-2">
            <TextField label="Code postal" required value={form.postal_code ?? ''}
              error={errors.postal_code} disabled={!isAdmin}
              onChange={(e) => set({ postal_code: e.target.value })} />
            <TextField label="Commune" required value={form.city ?? ''} error={errors.city}
              disabled={!isAdmin} onChange={(e) => set({ city: e.target.value })} />
            <TextField label="Téléphone" value={form.phone ?? ''} error={errors.phone}
              disabled={!isAdmin} onChange={(e) => set({ phone: e.target.value })} />
            <TextField label="E-mail" value={form.email ?? ''} error={errors.email}
              disabled={!isAdmin} onChange={(e) => set({ email: e.target.value })} />
          </div>
          <TextField label="IBAN" value={form.iban ?? ''} error={errors.iban}
            disabled={!isAdmin} onChange={(e) => set({ iban: e.target.value })} />
          <Textarea label="Mentions légales" rows={4} value={form.legal ?? ''} error={errors.legal}
            disabled={!isAdmin}
            hint="Conditions générales, réserve de propriété. Imprimées en bas du devis. Vide tant que Charles ne les a pas fournies."
            onChange={(e) => set({ legal: e.target.value })} />
        </form>
      </CardBody>
    </Card>
  );
}

export default SocietePanel;
